import React from 'react';
import {
    Box,
    Container,
    Divider,
    Paper,
    Typography
} from '@mui/material';

const LAST_UPDATED = 'June 5, 2026';

const policyContent = {
    privacy: {
        title: 'Privacy Policy',
        intro: 'This Privacy Policy explains how Botmark collects, uses, shares, and protects information when you use the Botmark website, portal, and related services.',
        sections: [
            {
                heading: 'Information we collect',
                body: 'We collect information you provide directly, information generated when you use the portal, and limited technical information needed to operate the service.',
                items: [
                    'Account information such as name, username, email address, company name, phone number, and mailing or billing contact details.',
                    'Company and project information submitted through the portal, including plan files, validation files, invoices, surveys, messages, licensing information, ruler information, and related metadata.',
                    'Authentication and security information, including login status, user role, session details, and policy acceptance records.',
                    'Payment and billing workflow information needed to prepare estimates, invoices, and account records. Payment processors may collect payment details under their own privacy terms.',
                    'Technical information such as browser type, device information, IP address, request timestamps, and diagnostic logs that help keep the service secure and reliable.'
                ]
            },
            {
                heading: 'How we use information',
                body: 'We use collected information to provide and improve the service, support users, and maintain legal and operational records.',
                items: [
                    'Create, authenticate, and administer user accounts.',
                    'Process uploaded files, downloads, estimates, invoices, licensing records, surveys, messages, and support requests.',
                    'Communicate with users about account activity, service updates, billing, support, and security.',
                    'Monitor service performance, troubleshoot issues, prevent misuse, and protect the security of users and the platform.',
                    'Maintain records required for business, accounting, compliance, and dispute-resolution purposes.'
                ]
            },
            {
                heading: 'How we share information',
                body: 'We do not sell personal information. We share information only as needed to operate Botmark and meet business, legal, or security obligations.',
                items: [
                    'With companies, processors, downloaders, billing users, and other account participants when their portal permissions require access to the relevant project, invoice, message, survey, or file data.',
                    'With vendors and service providers that host, secure, process, or support the application, subject to appropriate confidentiality and security expectations.',
                    'With payment, accounting, or business operations providers when needed to process billing or maintain required records.',
                    'When required by law, legal process, or a good-faith need to protect rights, safety, security, or the integrity of the service.',
                    'In connection with a merger, acquisition, financing, reorganization, or transfer of business assets, subject to continued protection of the information.'
                ]
            },
            {
                heading: 'Data retention',
                body: 'We keep information for as long as needed to provide the service, maintain user and company records, meet legal or accounting requirements, resolve disputes, and enforce agreements. Retention periods vary based on the type of information and business need.'
            },
            {
                heading: 'Security',
                body: 'We use administrative, technical, and organizational safeguards designed to protect information. No online service can guarantee absolute security, so users should protect their login credentials and notify us of suspected unauthorized access.'
            },
            {
                heading: 'Your choices',
                body: 'You may update account information in the portal where available or contact us to request help accessing, correcting, or deleting information. Some information may be retained when required for legal, security, billing, or legitimate business purposes.'
            },
            {
                heading: 'Children',
                body: 'Botmark is intended for business use and is not directed to children under 13. We do not knowingly collect personal information from children under 13.'
            },
            {
                heading: 'Changes to this policy',
                body: 'We may update this Privacy Policy when our practices or legal requirements change. The updated policy will be posted in the application with a new last updated date.'
            }
        ]
    },
    cookies: {
        title: 'Cookie Policy',
        intro: 'This Cookie Policy explains how Botmark uses cookies, browser storage, and similar technologies in the Botmark website and portal.',
        sections: [
            {
                heading: 'What cookies and browser storage are',
                body: 'Cookies are small files placed on a device by a website. Browser storage, such as session storage, lets a web application store limited information in the browser for the current session or user experience.'
            },
            {
                heading: 'How Botmark uses cookies and storage',
                body: 'The current Botmark frontend uses browser session storage to support essential application behavior. It stores authentication state after login and remembers the last selected top-level tab so the portal can continue to function during the browser session.',
                items: [
                    'Authentication storage keeps users signed in while they navigate the portal.',
                    'Navigation storage remembers the most recent portal tab selection.',
                    'Security and infrastructure cookies may be set by hosting, API, or security services when required for delivery, authentication, load balancing, or protection against misuse.'
                ]
            },
            {
                heading: 'Non-essential cookies',
                body: 'We did not identify frontend analytics, advertising, or social media tracking cookies in this application. If Botmark later adds non-essential cookies, this policy should be updated and any required consent controls should be added before those cookies are used.'
            },
            {
                heading: 'Managing cookies',
                body: 'Most browsers let you block, delete, or limit cookies and site storage. Blocking essential cookies or browser storage may prevent login, portal navigation, uploads, downloads, or other core Botmark features from working correctly.'
            },
            {
                heading: 'Changes to this policy',
                body: 'We may update this Cookie Policy when technologies, vendors, or legal requirements change. The updated policy will be posted in the application with a new last updated date.'
            }
        ]
    }
};

const PolicyPage = ({ type }) => {
    const policy = policyContent[type] ?? policyContent.privacy;

    return (
        <Box sx={{ py: 4, minHeight: '100%', backgroundColor: 'background.default' }}>
            <Container maxWidth="md">
                <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
                    <Typography variant="h3" component="h1" align="center" gutterBottom>
                        {policy.title}
                    </Typography>
                    <Typography variant="subtitle1" align="center" color="text.secondary" gutterBottom>
                        Last updated: {LAST_UPDATED}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 3 }}>
                        {policy.intro}
                    </Typography>
                    <Divider sx={{ my: 3 }} />
                    {policy.sections.map((section) => (
                        <Box key={section.heading} sx={{ mb: 3 }}>
                            <Typography variant="h5" component="h2" gutterBottom>
                                {section.heading}
                            </Typography>
                            <Typography variant="body1" paragraph>
                                {section.body}
                            </Typography>
                            {section.items && (
                                <Typography component="ul" variant="body1" sx={{ pl: 3, my: 1 }}>
                                    {section.items.map((item) => (
                                        <Typography component="li" key={item} sx={{ mb: 0.5 }}>
                                            {item}
                                        </Typography>
                                    ))}
                                </Typography>
                            )}
                        </Box>
                    ))}
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h5" component="h2" gutterBottom>
                        Contact
                    </Typography>
                    <Typography variant="body1">
                        If you have questions about this policy or Botmark data practices, please contact Botmark
                        support through your usual support channel.
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default PolicyPage;
